/**
 * Boot-time version check — forces a hard reload when a returning user lands
 * on a stale cached bundle.
 *
 * The flow:
 *   1. On every app boot, fetch /api/version with cache-busting + cache: 'no-store'.
 *   2. Compare to the version we stored on the user's LAST successful boot
 *      (localStorage key 'ckk_app_version').
 *   3. If different AND we haven't already auto-reloaded in this tab session,
 *      flag sessionStorage, unregister any stale service workers, then call
 *      window.location.reload(). The reload gets a fresh index.html + JS.
 *   4. After boot finishes successfully, update localStorage to the current
 *      version so subsequent visits compare against the latest.
 *
 * Returns a Promise<{ reloading: boolean }>. Caller should gate React.render
 * on { reloading: false }.
 *
 * Safe on preview/dev — fetch failure is silently ignored, app continues.
 * Subscription/auth cookies survive reload (same origin, same cookie jar).
 */

const VERSION_KEY = 'ckk_app_version';
const RELOADED_THIS_TAB_KEY = 'ckk_reloaded_after_version_mismatch';

function getBackendBase() {
  // Prefer same-origin rewriter's target if already active (custom domain),
  // otherwise use env backend URL directly. Avoid importing the rewriter so
  // this file has zero dependencies.
  const host = (typeof window !== 'undefined') ? window.location.hostname : '';
  if (host === 'ckkcriseofthedukes.online' || host === 'www.ckkcriseofthedukes.online') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
}

export async function bootVersionCheck() {
  if (typeof window === 'undefined') return { reloading: false };

  // Anti-loop guard — if we already auto-reloaded in this tab, don't loop.
  let alreadyReloaded = false;
  try { alreadyReloaded = sessionStorage.getItem(RELOADED_THIS_TAB_KEY) === '1'; } catch {}

  const base = getBackendBase();
  if (!base) return { reloading: false };

  // Timeout wrapper so a slow/unreachable backend never blocks boot.
  let version;
  try {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 4000);
    const resp = await fetch(`${base}/api/version?t=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal
    });
    clearTimeout(t);
    if (!resp.ok) return { reloading: false };
    const data = await resp.json();
    version = data && data.version;
  } catch {
    return { reloading: false };
  }
  if (!version) return { reloading: false };

  let lastSeen = null;
  try { lastSeen = localStorage.getItem(VERSION_KEY); } catch {}

  // First-ever visit (no recorded version). Just store it.
  if (!lastSeen) {
    try { localStorage.setItem(VERSION_KEY, version); } catch {}
    return { reloading: false };
  }

  // Same version — nothing to do.
  if (lastSeen === version) return { reloading: false };

  // Version drift detected.
  if (alreadyReloaded) {
    // We already reloaded this tab once — don't reload again, just update the
    // recorded version so future drifts trigger only one reload each.
    try { localStorage.setItem(VERSION_KEY, version); } catch {}
    return { reloading: false };
  }

  // Fresh drift → record the new version, unregister any stale service
  // workers, then hard-reload.
  try { sessionStorage.setItem(RELOADED_THIS_TAB_KEY, '1'); } catch {}
  try { localStorage.setItem(VERSION_KEY, version); } catch {}

  try {
    if ('serviceWorker' in navigator && navigator.serviceWorker.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister().catch(() => {})));
    }
  } catch { /* ignore */ }

  // Clear Cache Storage (sometimes seeded by SW or by old PWA manifest)
  try {
    if (typeof caches !== 'undefined' && caches.keys) {
      const names = await caches.keys();
      await Promise.all(names.map(n => caches.delete(n).catch(() => {})));
    }
  } catch { /* ignore */ }

  // Hard reload — bypasses bfcache, revalidates index.html, fresh chunks.
  window.location.reload();
  return { reloading: true };
}

export default bootVersionCheck;
